import * as React from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Icon } from "./icon";

/* The Sirdab empty state. The house "nothing here" treatment, promoted from
   the shell (notifications panel, search palette) so every empty surface
   speaks in one voice: a quiet icon WELL (--muted is the documented fill
   for wells), the message at body-quiet, an optional quieter line saying
   what will appear here, and an optional action slot for the one thing the
   person can do about it.

   Deliberately NOT shadcn's Empty family (Empty/EmptyHeader/EmptyMedia/
   EmptyTitle/EmptyContent): that is a roomier page-level composition, and
   TOKENS.md's standing note applies: shadcn's default compositions are
   roomier and more boxed than dense tooling. Our empty states live inside
   popovers and command lists, so the compact single-component shape wins.

   The icon is decorative: <Icon> is aria-hidden by default and the title
   carries the meaning, so nothing is announced twice. Idle-vs-no-results
   phrasing is the CALLER's decision (an invitation is not a result);
   this component only owns the shape.

   `className` is layout-only: the surface hosting the state owns its own
   padding and borders (a popover's py-8, a command list's py-6). */
function EmptyState({
  icon,
  title,
  caption,
  action,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  icon: LucideIcon;
  title: React.ReactNode;
  /** The quieter second line: what will appear here, not what is missing. */
  caption?: React.ReactNode;
  /** One action, a house Button; never a row of them. */
  action?: React.ReactNode;
}) {
  return (
    <div
      data-slot="empty-state"
      className={cn("flex flex-col items-center gap-3 text-center", className)}
      {...props}
    >
      <span className="bg-muted text-muted-foreground flex size-10 items-center justify-center rounded-full">
        <Icon icon={icon} className="size-5" />
      </span>
      <span className="flex flex-col gap-1">
        <span className="text-body-quiet text-muted-foreground">{title}</span>
        {caption ? (
          <span className="text-caption-quiet text-subtle">{caption}</span>
        ) : null}
      </span>
      {action}
    </div>
  );
}

export { EmptyState };
